import { useCallback, useEffect, useState } from "react";
import { LockKeyhole, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

export const SECURE_KEY = "ms_secure";
const EVT = "secure-change";

export const secureToken = () => {
  try {
    const s = JSON.parse(sessionStorage.getItem(SECURE_KEY) || "null");
    return s && new Date(s.expires_at) > new Date() ? s.token : null;
  } catch {
    return null;
  }
};
export const setSecure = (v) => {
  if (v) sessionStorage.setItem(SECURE_KEY, JSON.stringify(v));
  else sessionStorage.removeItem(SECURE_KEY);
  window.dispatchEvent(new Event(EVT));
};

export function useSecure() {
  const [unlocked, setUnlocked] = useState(!!secureToken());
  useEffect(() => {
    const upd = () => setUnlocked(!!secureToken());
    window.addEventListener(EVT, upd);
    const t = setInterval(upd, 30000);
    return () => { window.removeEventListener(EVT, upd); clearInterval(t); };
  }, []);
  return unlocked;
}

function UnlockForm({ configured, onDone, description }) {
  const [pwd, setPwd] = useState("");
  const [account, setAccount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = configured
        ? await api.post("/admin/secure/unlock", { password: pwd })
        : await api.post("/admin/secure/setup", { password: pwd, account_password: account });
      onDone(data);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mx-auto max-w-sm space-y-3 rounded-2xl border bg-white p-6 text-center" data-testid="secure-gate-form">
      <LockKeyhole className="mx-auto h-7 w-7 text-[#61a229]" />
      <p className="font-display text-lg font-semibold">{configured ? "Area protetta" : "Imposta la password protetta"}</p>
      <p className="text-xs text-slate-500">{configured ? (description || "Inserisci la password protetta per vedere timbrature e posizioni. Resta sbloccata per 30 minuti.")
        : "Scegli una seconda password, diversa da quella di accesso, per proteggere timbrature e posizioni dei tecnici."}</p>
      {!configured && <Input type="password" placeholder="Password del tuo account" value={account} onChange={(e) => setAccount(e.target.value)} required data-testid="secure-account-password" />}
      <Input type="password" placeholder={configured ? "Password protetta" : "Nuova password protetta (min. 6)"} value={pwd} onChange={(e) => setPwd(e.target.value)} required data-testid="secure-password-input" />
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="secure-error">{err}</p>}
      <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="secure-submit-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {configured ? "Sblocca" : "Imposta e sblocca"}
      </Button>
    </form>
  );
}

export function SecureGate({ children, description }) {
  const unlocked = useSecure();
  const [configured, setConfigured] = useState(null);
  const load = useCallback(() => api.get("/admin/secure/status").then((r) => setConfigured(r.data.configured)), []);
  useEffect(() => { if (!unlocked) load(); }, [unlocked, load]);
  if (unlocked) return children;
  if (configured === null) return <Loader2 className="h-5 w-5 animate-spin" />;
  return <UnlockForm configured={configured} onDone={setSecure} description={description} />;
}

export function SecureLockButton() {
  const unlocked = useSecure();
  if (!unlocked) return null;
  return <Button variant="outline" size="sm" className="rounded-full" onClick={() => setSecure(null)} data-testid="secure-lock-button"><LockKeyhole className="h-4 w-4" /> Blocca</Button>;
}
